if (!customElements.get("video-reels")) {
  customElements.define(
    "video-reels",
    class VideoReels extends HTMLElement {
      constructor() {
        super();
        this.activeClass = "active";
        this.posterActiveClass = "video-carousel--video-active";
        this.posterSelector = "[data-video-reels-poster]";
        this.videoTemplateSelector = "[data-video-reels-template]";
        this.swiper = this.querySelector(".wt-slider__container");
      }

      connectedCallback() {
        this._sectionObserver = null;
        this._onResize = this.handleResize.bind(this);
        window.addEventListener("resize", this._onResize);
        if (!this.isYdVideo()) this.initPosterHandlers();
        this.init();
      }

      handleResize() {
        const isDesktop = window.matchMedia("(min-width: 1025px)").matches;
        const hasSwiper = this.swiper?.classList.contains("swiper-initialized");

        if (isDesktop && !hasSwiper) {
          this.initDesktopGrid();
        }
      }

      disconnectedCallback() {
        if (this._sectionObserver) {
          this._sectionObserver.disconnect();
          this._sectionObserver = null;
        }
        if (this._desktopObserver) {
          this._desktopObserver.disconnect();
          this._desktopObserver = null;
        }
        if (this._onResize) {
          window.removeEventListener("resize", this._onResize);
        }
        if (this._onPosterClick) {
          this.removeEventListener("click", this._onPosterClick);
        }
        if (this._onPosterHover) {
          this.querySelectorAll("[data-swiper-slide]").forEach((slide) => {
            slide.removeEventListener("mouseenter", this._onPosterHover);
          });
        }
        if (this._onPosterLeave) {
          this.querySelectorAll("[data-swiper-slide]").forEach((slide) => {
            slide.removeEventListener("mouseleave", this._onPosterLeave);
          });
        }
        if (this.checkInterval) clearInterval(this.checkInterval);
      }

      // Playback is owned by yd-video.js; this element only tracks the active slide.
      isYdVideo() {
        return this.hasAttribute("data-yd-reels");
      }

      isClickToPlay() {
        return this.hasAttribute("data-click-to-play");
      }

      hasPoster(slide) {
        return Boolean(slide?.querySelector(this.posterSelector));
      }

      isPosterActivated(slide) {
        return !this.hasPoster(slide) || slide.classList.contains(this.posterActiveClass);
      }

      isTouchPlaybackMode() {
        return !window.matchMedia("(hover: hover) and (pointer: fine)").matches;
      }

      shouldPreloadVideosOnMobile() {
        return window.matchMedia("(max-width: 1024px)").matches;
      }

      prepareVideoForPlayback(video) {
        video.muted = video.muted !== false;
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "");
        video.removeAttribute("autoplay");
        video.autoplay = false;
        video.preload = "auto";
      }

      prepareVideoForPreload(video) {
        video.pause();
        video.muted = true;
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "");
        video.removeAttribute("autoplay");
        video.autoplay = false;
        video.preload = "auto";

        let framePrimed = false;
        const primeFrame = () => {
          if (framePrimed) return;
          framePrimed = true;

          try {
            const frameTime = Number.isFinite(video.duration) && video.duration > 0
              ? Math.min(0.01, video.duration)
              : 0.01;
            video.currentTime = frameTime;
          } catch (error) {
            console.warn("Video frame preload was prevented:", error);
          }
        };

        video.addEventListener("loadedmetadata", primeFrame, { once: true });
        video.load();

        if (video.readyState >= 1) {
          primeFrame();
        }
      }

      getSlideVideo(slide) {
        if (!slide) return null;

        const existingVideo = slide.querySelector("video");
        if (existingVideo) return existingVideo;

        const template = slide.querySelector(this.videoTemplateSelector);
        const movie = slide.querySelector(".wt-video__movie");
        if (!template || !movie) return null;

        const fragment = template.content.cloneNode(true);
        const video = fragment.querySelector("video");
        if (!video) return null;

        movie.appendChild(fragment);
        if (this.isClickToPlay()) {
          video.loop = false;
          video.removeAttribute("loop");
          video.preload = "none";
          video.setAttribute("controls", "");
          video.controls = true;
        }
        this.bindDeferredVideoControls(slide, video);

        return video;
      }

      preloadMobileVideos(swiper) {
        if (this.isClickToPlay()) return;
        if (!this.shouldPreloadVideosOnMobile() || this._mobileVideosPreloaded) return;

        this._mobileVideosPreloaded = true;
        const slides = swiper?.slides || this.querySelectorAll("[data-swiper-slide]");

        slides.forEach((slide) => {
          const video = this.getSlideVideo(slide);
          if (!video) return;

          this.prepareVideoForPreload(video);
        });
      }

      syncControlButton(controlButton, video) {
        if (!controlButton || !video) return;

        const isPlaying = !video.paused;
        controlButton.classList.toggle(
          "shoppable-video__control-button--play",
          isPlaying,
        );
        controlButton.classList.toggle(
          "shoppable-video__control-button--pause",
          !isPlaying,
        );
        controlButton.setAttribute("aria-pressed", isPlaying ? "true" : "false");
      }

      bindDeferredVideoControls(slide, video) {
        const controlButton = slide.querySelector(".shoppable-video__control-button");
        if (controlButton && !controlButton._boundDeferredVideo) {
          controlButton._boundDeferredVideo = true;

          controlButton.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();

            if (this.hasPoster(slide) && this.isTouchPlaybackMode()) {
              this.resetPoster(slide);
              return;
            }

            if (video.paused || video.ended) {
              video.play().catch(() => {});
            } else {
              video.pause();
            }
          });

          video.addEventListener("play", () => this.syncControlButton(controlButton, video));
          video.addEventListener("pause", () => this.syncControlButton(controlButton, video));
          this.syncControlButton(controlButton, video);
        }

        if (!video._boundPosterReset) {
          video._boundPosterReset = true;

          video.addEventListener("click", (event) => {
            if (!this.hasPoster(slide) || !this.isTouchPlaybackMode()) return;

            event.preventDefault();
            event.stopPropagation();
            this.resetPoster(slide);
          });
        }

        const soundButton = slide.querySelector(".wt-video__sound-toggle");
        if (soundButton && !soundButton._bound && !soundButton._boundDesktop && !soundButton._boundDeferredVideo) {
          soundButton._boundDeferredVideo = true;

          soundButton.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();

            video.muted = !video.muted;
            soundButton.dataset.sound = video.muted ? "off" : "on";
            soundButton.setAttribute("aria-pressed", video.muted ? "false" : "true");
          });
        }
      }

      showReelLoader(slide) {
        const loader = slide?.querySelector(".yd-video-loader");
        if (loader) loader.hidden = false;
      }

      hideReelLoader(slide) {
        const loader = slide?.querySelector(".yd-video-loader");
        if (loader) loader.hidden = true;
      }

      playOnClick(slide) {
        const video = this.getSlideVideo(slide);
        if (!video) return;

        video.controls = true;
        video.setAttribute("controls", "");
        video.loop = false;
        video.removeAttribute("loop");
        video.preload = "auto";
        video.playsInline = true;
        video.setAttribute("playsinline", "");
        video.setAttribute("webkit-playsinline", "");
        video.muted = false;
        video.removeAttribute("muted");
        this.showReelLoader(slide);

        const reveal = () => {
          this.hideReelLoader(slide);
          slide.classList.add(this.posterActiveClass);
        };

        const playPromise = video.play();
        if (playPromise && playPromise.then) {
          playPromise.then(reveal).catch(() => {
            video.muted = true;
            video.setAttribute("muted", "");
            video.play().then(reveal).catch(() => this.hideReelLoader(slide));
          });
          return;
        }

        reveal();
      }

      playSlideVideo(slide) {
        const video = this.getSlideVideo(slide);
        if (!video) return;

        this.prepareVideoForPlayback(video);
        video.play().catch((err) => {
          console.warn("Autoplay was prevented:", err);
        });
      }

      activatePoster(slide) {
        if (!slide) return;

        if (this.isClickToPlay()) {
          this.playOnClick(slide);
          return;
        }

        slide.classList.add(this.posterActiveClass);
        this.playSlideVideo(slide);
      }

      resetPoster(slide) {
        if (!slide || !this.hasPoster(slide)) return;

        const video = slide.querySelector("video");
        if (video) {
          video.pause();
          try {
            video.currentTime = 0;
          } catch (error) {
            console.warn("Video reset was prevented:", error);
          }
        }

        slide.classList.remove(this.posterActiveClass);
        this.hideReelLoader(slide);
      }

      handlePosterClick(event) {
        const poster = event.target.closest(this.posterSelector);
        if (!poster) return;

        event.preventDefault();
        event.stopPropagation();

        const slide = poster.closest("[data-swiper-slide]");
        const swiper = this.swiper?.swiper;
        if (swiper && slide && slide !== swiper.slides[swiper.activeIndex]) {
          const slideIndex = Array.from(swiper.slides).indexOf(slide);
          if (slideIndex >= 0) {
            swiper.slideTo(slideIndex);
          }
        }

        this.activatePoster(slide);
      }

      handlePosterHover(event) {
        if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

        this.activatePoster(event.currentTarget);
      }

      handlePosterLeave(event) {
        if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;

        this.resetPoster(event.currentTarget);
      }

      initPosterHandlers() {
        if (this._posterHandlersInitialized) return;
        this._posterHandlersInitialized = true;

        this._onPosterClick = this.handlePosterClick.bind(this);
        this._onPosterHover = this.handlePosterHover.bind(this);
        this._onPosterLeave = this.handlePosterLeave.bind(this);
        this.addEventListener("click", this._onPosterClick);
        if (this.isClickToPlay()) {
          this.addEventListener("pointerdown", (event) => {
            if (!event.target.closest(this.posterSelector)) return;
            event.stopPropagation();
          }, true);
          return;
        }

        this.querySelectorAll("[data-swiper-slide]").forEach((slide) => {
          if (!this.hasPoster(slide) || slide._videoReelsPosterHoverBound) return;
          slide._videoReelsPosterHoverBound = true;
          slide.addEventListener("mouseenter", this._onPosterHover);
          slide.addEventListener("mouseleave", this._onPosterLeave);
        });
      }

      observeSection() {
        const observerOptions = {
          root: null,
          rootMargin: "0px",
          threshold: 0.1,
        };

        const sectionObserver = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              const video = this.querySelector(".swiper-slide-active video");
              const slide = video?.closest("[data-swiper-slide]");

              if (video) {
                if (this.isClickToPlay()) {
                  if (!entry.isIntersecting) video.pause();
                  return;
                }

                if (entry.isIntersecting) {
                  if (this.isPosterActivated(slide)) {
                    this.playSlideVideo(slide);
                  }
                } else {
                  video.pause();
                }
              }
            });
          },
          observerOptions,
        );

        sectionObserver.observe(this);
        this._sectionObserver = sectionObserver;
      }

      hardStopAllExceptActive(swiper) {
        const active = swiper.slides[swiper.activeIndex];
        swiper.slides.forEach((slide) => {
          const video = slide.querySelector("video");
          if (!video) return;

          //Stop and mute all except active
          if (slide !== active) {
            video.pause();
            video.muted = true;
            video.removeAttribute("autoplay"); // avoid Safari storms
            video.autoplay = false;
          }
        });
      }

      handleSoundToggle(swiper) {
        swiper.slides.forEach((slide) => {
          const button = slide.querySelector(".wt-video__sound-toggle");
          if (!button || button._bound) return; // bind only once
          button._bound = true;

          button.addEventListener(
            "click",
            () => {
              const activeSlide = swiper.slides[swiper.activeIndex];
              const activeVideo = activeSlide?.querySelector("video");
              if (!activeVideo) return;

              // stop and mute everything except active
              this.hardStopAllExceptActive(swiper);

              // toggle global state and apply ONLY to active video
              const soundOn = swiper.el.dataset.sound === "on";
              const nextSoundOn = !soundOn;
              swiper.el.dataset.sound = nextSoundOn ? "on" : "off";
              activeVideo.muted = !nextSoundOn;

              if (this.isClickToPlay()) return;

              // inline playback hints for Safari/iOS
              activeVideo.setAttribute("playsinline", "");
              activeVideo.setAttribute("webkit-playsinline", "");
              activeVideo.removeAttribute("autoplay");
              activeVideo.autoplay = false;

              // (re)play only the active one; tiny delay helps Safari
              setTimeout(() => {
                activeVideo.play().catch(() => {});
              }, 80);
            },
            { passive: true },
          );
        });
      }

      sanitizeVideosOnce() {
        if (this._sanitized) return;
        this._sanitized = true;
        this.querySelectorAll("video").forEach((video) => {
          video.pause();
          video.muted = true;
          video.removeAttribute("autoplay");
          video.autoplay = false;
          video.setAttribute("playsinline", "");
          video.setAttribute("webkit-playsinline", "");
          const hasPoster = this.hasPoster(video.closest("[data-swiper-slide]"));
          video.preload = this.shouldPreloadVideosOnMobile() ? "auto" : hasPoster ? "none" : "metadata";
        });
      }

      playVideoInActiveSlide(swiper) {
        const sound = swiper.el.dataset.sound;
        const activeSlide = this.findActiveSlide(swiper);
        if (!this.isPosterActivated(activeSlide)) return;

        const activeSlideVideo = activeSlide?.querySelector("video");
        if (activeSlideVideo) {
          activeSlideVideo.muted = sound !== "on"; // if sound !== "on", keep it muted

          setTimeout(() => {
            activeSlideVideo.play().catch((err) => {
              console.warn("Autoplay was prevented:", err);
            });
          }, 100);
        }
      }

      findActiveSlide(swiper) {
        const activeSlide = swiper.slides[swiper.activeIndex];
        return activeSlide;
      }

      toggleActiveClass(swiper) {
        const activeSlide = this.findActiveSlide(swiper);
        swiper.slides.forEach((slide) => {
          slide.classList.remove(this.activeClass);

          if (activeSlide === slide) {
            slide.classList.add(this.activeClass);
          }
        });
      }

      handleSlideChange(swiper) {
        if (this.isClickToPlay()) {
          swiper.slides.forEach((slide) => this.resetPoster(slide));
          this.toggleActiveClass(swiper);
          return;
        }

        this.hardStopAllExceptActive(swiper);
        this.toggleActiveClass(swiper);
        this.playVideoInActiveSlide(swiper);
      }

      addVideoEventHandlers(swiper) {
        this._onSlideChange = () => this.handleSlideChange(swiper);
        swiper.on("slideChange", this._onSlideChange);
        swiper.on("slidesLengthChange", () => this.handleSoundToggle(swiper));
        swiper.on("update", () => this.handleSoundToggle(swiper));
      }

      checkSwiperInitialization() {
        const swiperContainer = this.swiper;
        const mySwiperInstance = swiperContainer?.swiper;

        if (
          swiperContainer?.classList.contains("swiper-initialized") &&
          mySwiperInstance
        ) {
          if (!this.swiper?.dataset.sound) {
            this.swiper.dataset.sound = "off";
          }
          if (this.isYdVideo()) {
            this.toggleActiveClass(mySwiperInstance);
            mySwiperInstance.on("slideChange", () =>
              this.toggleActiveClass(mySwiperInstance),
            );
            clearInterval(this.checkInterval);
            return;
          }
          this.preloadMobileVideos(mySwiperInstance);
          this.sanitizeVideosOnce();
          this.addVideoEventHandlers(mySwiperInstance);
          this.handleSlideChange(mySwiperInstance);
          this.handleSoundToggle(mySwiperInstance);
          this.observeSection();
          clearInterval(this.checkInterval);
          return;
        }

        if (window.matchMedia("(min-width: 1025px)").matches) {
          this._desktopCheckCount = (this._desktopCheckCount || 0) + 1;
          if (this._desktopCheckCount > 6) {
            this.initDesktopGrid();
            clearInterval(this.checkInterval);
          }
        }
      }

      initDesktopGrid() {
        if (this._desktopGridInitialized) return;
        this._desktopGridInitialized = true;

        const slides = this.querySelectorAll("[data-swiper-slide]");
        slides.forEach((slide) => {
          slide.classList.add(this.activeClass);
        });

        if (this.isYdVideo()) return;

        this.sanitizeVideosOnce();

        if (this.isClickToPlay()) return;

        slides.forEach((slide) => {
          const button = slide.querySelector(".wt-video__sound-toggle");
          const video = slide.querySelector("video");
          if (!button || !video || button._boundDesktop) return;
          button._boundDesktop = true;

          button.addEventListener("click", () => {
            const soundOn = button.dataset.sound === "on";
            const nextSoundOn = !soundOn;
            button.dataset.sound = nextSoundOn ? "on" : "off";
            video.muted = !nextSoundOn;
            video.setAttribute("playsinline", "");
            video.setAttribute("webkit-playsinline", "");
            video.play().catch(() => {});
          });
        });

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const video = entry.target.querySelector("video");
              if (!video) return;

              if (entry.isIntersecting) {
                if (this.isClickToPlay()) return;
                if (this.isPosterActivated(entry.target)) {
                  this.playSlideVideo(entry.target);
                }
              } else {
                video.pause();
              }
            });
          },
          { threshold: 0.35 },
        );

        slides.forEach((slide) => observer.observe(slide));
        this._desktopObserver = observer;
      }

      init() {
        this.checkInterval = setInterval(
          this.checkSwiperInitialization.bind(this),
          250,
        );
      }
    },
  );
}
